import { Dispatch, SetStateAction, useContext, useMemo } from 'react';

import { useMutation } from '@apollo/client';
import { IContextMenuItem } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import s from 'pages/Clients/Clients.module.scss';
import { GroupTitle, TClient } from 'pages/Clients/interfaces';
import {
  CHANGE_USERS_GROUP,
  IClientsResponse,
  TChangeUsersGroupRequest,
  TChangeUsersGroupResponse,
} from 'schemas';

export const useContextMenuItems = (
  groups: GroupTitle[] | undefined,
  data: IClientsResponse | undefined,
  uuids: string[],
  setSelectedClientsUUIDs: Dispatch<SetStateAction<string[]>>,
  setDoubleClickedClientUUID: Dispatch<SetStateAction<string | null>>,
  setNotPermissionModalOpen: Dispatch<SetStateAction<boolean>>
): IContextMenuItem<TClient>[] => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [changeUserGroup] = useMutation<
    TChangeUsersGroupResponse,
    TChangeUsersGroupRequest
  >(CHANGE_USERS_GROUP, {
    onCompleted: () => setSelectedClientsUUIDs([]),
    update: (cache, _, { variables }) => {
      const clients = data?.clients;
      if (variables && clients && groups) {
        const { group_uuid, users_uuids } = variables.input;
        const uuidSet = new Set(users_uuids);
        const group = groups.find(({ uuid }) => uuid === group_uuid) ?? {};

        cache.modify({
          fields: {
            clients: () => ({
              ...clients,
              data: clients.data.map((client) =>
                uuidSet.has(client.uuid) ? { ...client, group } : client
              ),
            }),
          },
        });
      }
    },
  });

  const uuidsLength = uuids.length;

  const groupsSubMenu: IContextMenuItem<TClient>[] = useMemo(() => {
    if (!groups) return [];
    const selectedClientsInGroup: Record<string, number> = {};
    const setClients = new Set(uuids);

    data?.clients.data.forEach(({ group, uuid: clientUUID }) => {
      if (setClients.has(clientUUID) && group) {
        if (!selectedClientsInGroup[group.uuid]) {
          selectedClientsInGroup[group.uuid] = 0;
        }

        selectedClientsInGroup[group.uuid] += 1;
      }
    });

    return (
      groups.map(({ uuid, title }) => ({
        key: uuid,
        text: title,
        disabled:
          (selectedClientsInGroup[uuid] &&
            selectedClientsInGroup[uuid] === uuidsLength) ||
          false,
        onClick: () => {
          if (uuidsLength > 0) {
            changeUserGroup({
              variables: {
                input: { group_uuid: uuid, users_uuids: uuids },
              },
            });
          }
        },
      })) || []
    );
  }, [groups, uuidsLength, data, uuids]);

  return useMemo(
    () => [
      {
        key: 'profile',
        text: t('clients.profile.open'),
        iconName: 'OpenedEyeIcon',
        onClick: ({ uuid }) => {
          if (!permissions?.has('client_info')) {
            setNotPermissionModalOpen(true);
            return;
          }
          setDoubleClickedClientUUID(uuid);
        },
      },
      {
        key: 'addToGroup',
        text: t('clients.group.add'),
        iconName: 'AddUserToGroupIcon',
        subMenu: groupsSubMenu,
        disabled: uuidsLength === 0 || !permissions?.has('client_manage'),
      },
      {
        key: 'deleteFromGroup',
        text: t('clients.group.remove'),
        iconName: 'RemoveUserFromGroupIcon',
        className: s.remove,
        disabled: uuidsLength === 0 || !permissions?.has('client_manage'),
        onClick: () => {
          changeUserGroup({
            variables: {
              input: { group_uuid: '', users_uuids: uuids },
            },
          });
        },
      },
    ],
    [uuidsLength, uuids, permissions]
  );
};
